import React from 'react';
import { UserSettings } from '../../types/index';

interface CalendarSettingsCardProps {
  preferences: UserSettings;
  onUpdate?: (preferences: UserSettings) => void;
}

const DAYS = [
  { key: 'monday', label: 'Lun' },
  { key: 'tuesday', label: 'Mar' },
  { key: 'wednesday', label: 'Mié' },
  { key: 'thursday', label: 'Jue' },
  { key: 'friday', label: 'Vie' },
  { key: 'saturday', label: 'Sáb' },
  { key: 'sunday', label: 'Dom' },
];

const CalendarSettingsCard: React.FC<CalendarSettingsCardProps> = ({ preferences, onUpdate }) => {
  const handleChange = (field: string, value: any) => {
    if (onUpdate) {
      onUpdate({
        ...preferences,
        [field]: value,
      });
    }
  };

  const handleDayToggle = (day: string) => {
    if (onUpdate) {
      onUpdate({
        ...preferences,
        availableDays: {
          ...preferences.availableDays,
          [day]: !preferences.availableDays[day as keyof typeof preferences.availableDays],
        },
      });
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md mb-6">
      <h2 className="text-xl font-semibold text-primary mb-4">Configuración de Citas</h2>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div>
            <h3 className="font-semibold text-gray-900">Activar citas</h3>
            <p className="text-sm text-gray-600">
              Permite que el asistente consulte disponibilidad, agende, modifique y cancele citas.
            </p>
          </div>
          <label className="relative inline-flex cursor-pointer items-center">
            <input
              type="checkbox"
              checked={preferences.appointmentsEnabled}
              onChange={(e) => handleChange('appointmentsEnabled', e.target.checked)}
              className="peer sr-only"
            />
            <span className="h-6 w-11 rounded-full bg-gray-300 transition-colors after:absolute after:left-0.5 after:top-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-transform peer-checked:bg-primary peer-checked:after:translate-x-5" />
          </label>
        </div>

        <div>
          <label className="block text-primary text-sm font-bold mb-2">Horario Laboral</label>
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-gray-600 text-sm mb-1">Inicio</label>
              <input
                type="time"
                value={preferences.workingHours.start}
                disabled={!preferences.appointmentsEnabled}
                onChange={(e) =>
                  handleChange('workingHours', {
                    ...preferences.workingHours,
                    start: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-100 disabled:text-gray-400"
              />
            </div>
            <div className="flex-1">
              <label className="block text-gray-600 text-sm mb-1">Fin</label>
              <input
                type="time"
                value={preferences.workingHours.end}
                disabled={!preferences.appointmentsEnabled}
                onChange={(e) =>
                  handleChange('workingHours', {
                    ...preferences.workingHours,
                    end: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-100 disabled:text-gray-400"
              />
            </div>
          </div>
        </div>

        <div>
          <label className="block text-primary text-sm font-bold mb-4">Días Disponibles</label>
          <div className="grid grid-cols-7 gap-2 p-4 bg-gray-50 rounded-xl border border-gray-200">
            {DAYS.map(({ key, label }) => {
              const isActive =
                preferences.availableDays[key as keyof typeof preferences.availableDays];
              return (
                <div key={key} className="flex items-center justify-center">
                  <button
                    type="button"
                    disabled={!preferences.appointmentsEnabled}
                    onClick={() => handleDayToggle(key)}
                    className={`
                      w-12 h-12 rounded-xl font-medium text-sm
                      transition-all duration-200 ease-in-out
                      focus:outline-none focus:ring-2 focus:ring-offset-1
                      ${
                        isActive
                          ? 'bg-secondary text-white shadow-md hover:bg-secondary/90 focus:ring-secondary'
                          : 'bg-white text-gray-400 border border-gray-200 hover:border-secondary hover:text-secondary focus:ring-gray-200'
                      }
                      disabled:cursor-not-allowed disabled:opacity-50
                    `}
                    aria-pressed={isActive}
                  >
                    {label}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <label className="block text-primary text-sm font-bold mb-2">
            Duración Predeterminada de Reuniones
          </label>
          <select
            value={preferences.defaultMeetingDuration}
            disabled={!preferences.appointmentsEnabled}
            onChange={(e) => handleChange('defaultMeetingDuration', parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-100 disabled:text-gray-400"
          >
            <option value="15">15 minutos</option>
            <option value="30">30 minutos</option>
            <option value="45">45 minutos</option>
            <option value="60">1 hora</option>
          </select>
        </div>

        <div>
          <label className="block text-primary text-sm font-bold mb-2">Zona Horaria</label>
          <select
            value={preferences.timezone}
            disabled={!preferences.appointmentsEnabled}
            onChange={(e) => handleChange('timezone', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-100 disabled:text-gray-400"
          >
            <optgroup label="Norte América">
              <option value="America/Mexico_City">Ciudad de México (UTC-6)</option>
              <option value="America/Tijuana">Tijuana (UTC-8)</option>
              <option value="America/Cancun">Cancún (UTC-5)</option>
              <option value="America/Monterrey">Monterrey (UTC-6)</option>
            </optgroup>

            <optgroup label="Centro América">
              <option value="America/Guatemala">Guatemala (UTC-6)</option>
              <option value="America/El_Salvador">El Salvador (UTC-6)</option>
              <option value="America/Tegucigalpa">Honduras (UTC-6)</option>
              <option value="America/Managua">Nicaragua (UTC-6)</option>
              <option value="America/Costa_Rica">Costa Rica (UTC-6)</option>
              <option value="America/Panama">Panamá (UTC-5)</option>
            </optgroup>

            <optgroup label="Caribe">
              <option value="America/Havana">Cuba (UTC-5)</option>
              <option value="America/Santo_Domingo">República Dominicana (UTC-4)</option>
              <option value="America/Puerto_Rico">Puerto Rico (UTC-4)</option>
            </optgroup>

            <optgroup label="Sudamérica">
              <option value="America/Bogota">Colombia (UTC-5)</option>
              <option value="America/Caracas">Venezuela (UTC-4)</option>
              <option value="America/Guayaquil">Ecuador (UTC-5)</option>
              <option value="America/Lima">Perú (UTC-5)</option>
              <option value="America/La_Paz">Bolivia (UTC-4)</option>
              <option value="America/Santiago">Chile (UTC-4)</option>
              <option value="America/Asuncion">Paraguay (UTC-4)</option>
              <option value="America/Montevideo">Uruguay (UTC-3)</option>
              <option value="America/Buenos_Aires">Argentina (UTC-3)</option>
            </optgroup>

            <optgroup label="Europa">
              <option value="Europe/Madrid">España (UTC+1)</option>
              <option value="Atlantic/Canary">Islas Canarias (UTC+0)</option>
            </optgroup>
          </select>
        </div>
      </div>
    </div>
  );
};

export default CalendarSettingsCard;
